import type { ReactNode } from 'react';
import { DataTableFooterShell } from './FooterShell';
import type { DataTablePagination } from './types';

const NAV_BUTTON_CLASS =
    'text-gray-400 text-xl px-2 py-1 rounded hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50';

/**
 * Canonical pagination footer - the « [page] of N (X results) » pager the
 * billing and accounts tables all use. Reconciled from the two duplicated
 * `TableControls.tsx` copies; the dashboard copy's `aria-label`s are kept,
 * since the files-viewer copy's unlabelled `«`/`»` buttons announce as
 * nothing useful.
 *
 * Server-side only: it renders the controls and calls back, holding no state.
 */
export interface DataTablePaginationBarProps extends DataTablePagination {
    /** The mode toggle, when the table has one - rendered at the row's end. */
    trailing?: ReactNode;
    className?: string;
}

export function DataTablePaginationBar({
    page,
    totalPages,
    total,
    onPage,
    trailing,
    className
}: DataTablePaginationBarProps) {
    const goTo = (next: number) => onPage(Math.min(Math.max(1, next), Math.max(1, totalPages)));
    return (
        <DataTableFooterShell ariaLabel="Pagination" trailing={trailing} className={className}>
            <button
                type="button"
                className={NAV_BUTTON_CLASS}
                onClick={() => goTo(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
            >
                &laquo;
            </button>
            <span className="mx-2">
                {total === undefined && (
                    <span className="mr-2 text-gray-400 text-base font-medium">Page</span>
                )}
                <span className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 rounded-lg bg-white text-gray-700 font-medium text-base select-none">
                    {page}
                </span>
                {total !== undefined && (
                    <>
                        <span className="mx-2 text-gray-400 text-base font-medium">of</span>
                        <span className="text-gray-700 text-base font-medium">{totalPages}</span>
                        <span className="ml-2 text-gray-500 text-sm">
                            ({total} result{total === 1 ? '' : 's'})
                        </span>
                    </>
                )}
            </span>
            <button
                type="button"
                className={NAV_BUTTON_CLASS}
                onClick={() => goTo(page + 1)}
                disabled={page >= totalPages}
                aria-label="Next page"
            >
                &raquo;
            </button>
        </DataTableFooterShell>
    );
}
